import { useTu } from "@src/appV2/i18n/useTu";
import { Stack } from "@mui/material";
import { type UseModalState } from "@src/lib/ui-react";
import { useState } from "react";
import { APP_V2_USER_EVENTS, logEvent, useLogEffect } from "../../../../lib/analytics";
import { Drawer } from "../../../components/Drawer";
import { Text } from "../../../components/Text";
import { useCreateSpendingControlNotification } from "../../api/useCreateSpendingControlNotification";
import { saveSpendingLimitIncreaseRequest } from "../../utils/spendingLimitRequest";
import { SpendingLimitIncreaseRequestForm } from "./SpendingLimitIncreaseRequestForm";

interface SpendingLimitIncreaseRequestDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  weekStartDate: Date;
  weekRange: string;
  currentLimitInDollars?: number;
  currentLimitInHours?: number;
  onSuccess?: () => void;
}

export function SpendingLimitIncreaseRequestModal(props: SpendingLimitIncreaseRequestDialogProps) {
  const { tu } = useTu("spendingControls");

  const {
    modalState,
    workplaceId,
    weekStartDate,
    weekRange,
    currentLimitInDollars,
    currentLimitInHours,
    onSuccess,
  } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutateAsync: createNotification } = useCreateSpendingControlNotification();

  useLogEffect(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_DRAWER_OPENED, {
    workplaceId,
    weekStartDate: weekStartDate.toISOString(),
    currentLimitInDollars,
    currentLimitInHours,
  });

  const handleSubmit = async (values: { additionalHours: number }) => {
    setIsSubmitting(true);

    try {
      // Save to localStorage
      saveSpendingLimitIncreaseRequest(workplaceId, weekStartDate, values.additionalHours);

      // Send Slack notification for the increase request
      // Notification failure should not block the user flow
      try {
        await createNotification({
          workplaceId,
          event: "increaseRequested",
          weekStartDate,
          additionalHoursRequested: values.additionalHours,
        });
      } catch {
        // Notification failure is non-critical, continue with the flow
      }

      logEvent(APP_V2_USER_EVENTS.SPENDING_CONTROL_INCREASE_REQUEST_SUBMITTED, {
        workplaceId,
        weekStartDate: weekStartDate.toISOString(),
        currentLimitInDollars,
        currentLimitInHours,
        additionalHours: values.additionalHours,
      });

      onSuccess?.();
      modalState.closeModal();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Drawer showCloseButton modalState={modalState} title={tu("Request Increase")}>
      <Stack spacing={4}>
        <Stack spacing={2}>
          <Text bold variant="h5">
            {weekRange}
          </Text>
          <Text variant="body1">
            {tu(
              "How many additional hours are needed for this week beyond the shifts already posted?"
            )}
          </Text>
        </Stack>

        <SpendingLimitIncreaseRequestForm isSubmitting={isSubmitting} onSubmit={handleSubmit} />
      </Stack>
    </Drawer>
  );
}
